import { useState, useMemo } from "react";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Slider } from "@/components/ui/slider";
import { Separator } from "@/components/ui/separator";
import { CurriculumSection } from "@/components/CurriculumSection";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Cell, ReferenceLine
} from "recharts";

// ─── Types & Data ──────────────────────────────────────────────────────────────
interface DCFInputs {
  revenue: number; growth1: number; growth2: number; growth3: number;
  ebitdaMargin: number; daPercent: number; capexPercent: number;
  nwcPercent: number; taxRate: number; wacc: number;
  tgr: number; netDebt: number; shares: number;
}
const DEFAULT: DCFInputs = {
  revenue: 500, growth1: 15, growth2: 12, growth3: 9,
  ebitdaMargin: 25, daPercent: 5, capexPercent: 6,
  nwcPercent: 2, taxRate: 17, wacc: 9,
  tgr: 2.5, netDebt: 80, shares: 100,
};

interface CompsRow { company: string; ev: number; revenue: number; ebitda: number; ni: number; }
const COMPS_DEFAULT: CompsRow[] = [
  { company: "Sea Limited",   ev: 19200, revenue: 3100, ebitda:  520, ni:  280 },
  { company: "Grab Holdings", ev:  7200, revenue: 2400, ebitda:  180, ni: -120 },
  { company: "GoTo Group",    ev:  4500, revenue: 1800, ebitda:   90, ni: -310 },
  { company: "Shopee (SEA)",  ev: 12800, revenue: 9200, ebitda: 1150, ni:  620 },
];

const FF_DEFAULT = [
  { label: "DCF",             low: 18.5, mid: 24.2, high: 31.0, color: "oklch(0.38 0.14 230)" },
  { label: "EV/EBITDA Comps", low: 20.0, mid: 25.5, high: 33.0, color: "oklch(0.52 0.14 150)" },
  { label: "EV/Revenue",      low: 15.0, mid: 21.0, high: 28.0, color: "oklch(0.55 0.16 280)" },
  { label: "P/E Comps",       low: 22.0, mid: 27.0, high: 35.0, color: "oklch(0.58 0.16 45)"  },
  { label: "Precedents",      low: 26.0, mid: 32.0, high: 40.0, color: "oklch(0.55 0.18 10)"  },
];

// ─── DCF Engine ────────────────────────────────────────────────────────────────
function runDCF(inp: DCFInputs) {
  const gr = [inp.growth1, inp.growth2, inp.growth3, inp.growth3 * 0.8, inp.tgr * 2];
  let rev = inp.revenue;
  const fcfs: number[] = [], pvs: number[] = [];
  for (let y = 0; y < 5; y++) {
    rev *= 1 + gr[y] / 100;
    const nopat = (rev * inp.ebitdaMargin / 100 - rev * inp.daPercent / 100) * (1 - inp.taxRate / 100);
    const fcf = nopat + rev * inp.daPercent / 100 - rev * inp.capexPercent / 100 - rev * inp.nwcPercent / 100;
    fcfs.push(fcf); pvs.push(fcf / Math.pow(1 + inp.wacc / 100, y + 1));
  }
  const pvFCF = pvs.reduce((a, b) => a + b, 0);
  const tv = (fcfs[4] * (1 + inp.tgr / 100)) / ((inp.wacc - inp.tgr) / 100);
  const pvTV = tv / Math.pow(1 + inp.wacc / 100, 5);
  const ev = pvFCF + pvTV;
  const eq = ev - inp.netDebt;
  return { fcfs, pvs, pvFCF, pvTV, ev, eq, pps: eq / inp.shares, tvPct: (pvTV / ev) * 100 };
}

// ─── Helpers ───────────────────────────────────────────────────────────────────
function fmt(n: number) { return Math.abs(n) >= 1000 ? `$${(n / 1000).toFixed(1)}B` : `$${n.toFixed(0)}M`; }
function fmtX(n: number | null) { if (!n || !isFinite(n) || n <= 0) return "NM"; return `${n.toFixed(1)}x`; }
function med(arr: number[]) {
  const s = [...arr].sort((a, b) => a - b); const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}

// ─── Slider Row ────────────────────────────────────────────────────────────────
function SRow({ label, value, unit, min, max, step, onChange }: {
  label: string; value: number; unit: string;
  min: number; max: number; step: number; onChange: (v: number) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex justify-between text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-mono font-semibold text-foreground tabular-nums">{value}{unit}</span>
      </div>
      <Slider min={min} max={max} step={step} value={[value]}
        onValueChange={([v]) => onChange(v)} className="w-full" />
    </div>
  );
}

// ─── Sensitivity Table ─────────────────────────────────────────────────────────
function SensTable({ inp }: { inp: DCFInputs }) {
  const waccs = [-2, -1, 0, 1, 2].map(d => inp.wacc + d);
  const tgrs  = [-1, -0.5, 0, 0.5, 1].map(d => inp.tgr + d);
  const base  = runDCF(inp).pps;
  return (
    <div className="overflow-x-auto">
      <p className="text-[10px] text-muted-foreground mb-1.5">Price/Share ($) · WACC rows · Terminal Growth Rate cols</p>
      <table className="w-full text-[10px] font-mono">
        <thead>
          <tr>
            <th className="text-left py-1 pr-3 text-muted-foreground font-medium">↓WACC / TGR→</th>
            {tgrs.map(t => <th key={t} className="px-2 py-1 text-center text-muted-foreground">{t.toFixed(1)}%</th>)}
          </tr>
        </thead>
        <tbody>
          {waccs.map(w => (
            <tr key={w} className="border-t border-border/40">
              <td className="py-1 pr-3 text-muted-foreground">{w.toFixed(1)}%</td>
              {tgrs.map(t => {
                const v = runDCF({ ...inp, wacc: w, tgr: t }).pps;
                const isBase = w === inp.wacc && t === inp.tgr;
                const pct = (v / base - 1) * 100;
                return (
                  <td key={t} className={`px-2 py-1 text-center tabular-nums rounded ${
                    isBase ? "bg-primary/10 text-primary font-bold ring-1 ring-primary/20" :
                    pct > 20 ? "text-emerald-700" : pct < -20 ? "text-rose-600" : "text-foreground"
                  }`}>{v > 0 ? `$${v.toFixed(1)}` : "—"}</td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Comps Table ───────────────────────────────────────────────────────────────
function CompsTable() {
  const [rows, setRows] = useState<CompsRow[]>(COMPS_DEFAULT);
  const update = (i: number, f: keyof CompsRow) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = [...rows];
    next[i] = { ...next[i], [f]: f === "company" ? e.target.value : parseFloat(e.target.value) || 0 };
    setRows(next);
  };
  const addRow = () => setRows(p => [...p, { company: "New Co", ev: 0, revenue: 0, ebitda: 0, ni: 0 }]);
  const evRevs = rows.map(r => r.ev / r.revenue).filter(v => isFinite(v) && v > 0);
  const evEbs  = rows.map(r => r.ebitda > 0 ? r.ev / r.ebitda : null).filter((v): v is number => v !== null && v > 0);
  const pes    = rows.map(r => r.ni > 0 ? (r.ev * 0.85) / r.ni : null).filter((v): v is number => v !== null && v > 0);
  const inp = "border border-border rounded px-1.5 py-0.5 text-xs font-mono w-full text-right bg-white focus:outline-none focus:ring-1 focus:ring-primary/40";
  return (
    <div className="space-y-4">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border">
              {["Company", "EV ($M)", "Revenue", "EBITDA", "Net Income"].map(h => (
                <th key={h} className={`py-2 font-medium text-muted-foreground ${h === "Company" ? "text-left pr-3" : "text-right px-2"}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border/50">
            {rows.map((r, i) => (
              <tr key={i}>
                <td className="py-1.5 pr-2">
                  <input className="border border-border rounded px-1.5 py-0.5 text-xs w-full bg-white focus:outline-none focus:ring-1 focus:ring-primary/40"
                    value={r.company} onChange={update(i, "company")} />
                </td>
                {(["ev", "revenue", "ebitda", "ni"] as const).map(f => (
                  <td key={f} className="py-1.5 px-1">
                    <input type="number" className={inp} value={r[f]} onChange={update(i, f)} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button onClick={addRow} className="text-xs text-primary hover:text-primary/70 transition-colors font-medium">+ Add company</button>
      <div>
        <p className="text-[10px] text-muted-foreground mb-1.5 uppercase tracking-wide font-medium">Computed Multiples</p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs font-mono">
            <thead>
              <tr className="border-b border-border">
                {["Company", "EV/Revenue", "EV/EBITDA", "P/E"].map(h => (
                  <th key={h} className={`py-2 font-medium text-muted-foreground not-italic font-sans ${h === "Company" ? "text-left pr-3" : "text-right px-3"}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border/50">
              {rows.map((r, i) => (
                <tr key={i} className="hover:bg-gray-50/60 transition-colors">
                  <td className="py-1.5 pr-3 text-foreground font-sans font-medium">{r.company}</td>
                  <td className="py-1.5 px-3 text-right tabular-nums">{fmtX(r.ev / r.revenue)}</td>
                  <td className="py-1.5 px-3 text-right tabular-nums">{fmtX(r.ebitda > 0 ? r.ev / r.ebitda : null)}</td>
                  <td className="py-1.5 pl-3 text-right tabular-nums">{fmtX(r.ni > 0 ? (r.ev * 0.85) / r.ni : null)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-primary/30 bg-primary/5">
                <td className="py-1.5 pr-3 font-semibold text-primary font-sans">Median</td>
                <td className="py-1.5 px-3 text-right tabular-nums font-bold text-primary">{fmtX(evRevs.length ? med(evRevs) : null)}</td>
                <td className="py-1.5 px-3 text-right tabular-nums font-bold text-primary">{fmtX(evEbs.length ? med(evEbs) : null)}</td>
                <td className="py-1.5 pl-3 text-right tabular-nums font-bold text-primary">{fmtX(pes.length ? med(pes) : null)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Football Field ────────────────────────────────────────────────────────────
function FootballBar({ ranges, cp }: { ranges: typeof FF_DEFAULT; cp: number }) {
  const allV = ranges.flatMap(r => [r.low, r.high]);
  const mn = Math.min(...allV) * 0.85, mx = Math.max(...allV) * 1.1, span = mx - mn;
  const cpPct = ((cp - mn) / span) * 100;
  return (
    <div className="space-y-4 relative pt-5 pb-6">
      <div className="absolute top-0 bottom-0 z-10 pointer-events-none border-l border-dashed border-foreground/25"
        style={{ left: `calc(7.5rem + ${cpPct / 100} * (100% - 7.5rem))` }} />
      {ranges.map((r, i) => {
        const lP = ((r.low - mn) / span) * 100;
        const wP = ((r.high - r.low) / span) * 100;
        const mP = ((r.mid - mn) / span) * 100;
        return (
          <div key={i} className="flex items-center gap-3">
            <div className="w-30 text-[11px] text-muted-foreground text-right shrink-0" style={{ width: "7.5rem" }}>{r.label}</div>
            <div className="flex-1 relative h-6 flex items-center">
              <div className="w-full h-px bg-border" />
              <div className="absolute h-4 rounded opacity-75"
                style={{ left: `${lP}%`, width: `${wP}%`, background: r.color }} />
              <div className="absolute w-0.5 h-5 rounded"
                style={{ left: `${mP}%`, background: r.color, filter: "brightness(0.75)" }} />
              <span className="absolute text-[9px] font-mono text-muted-foreground"
                style={{ left: `${lP}%`, top: "100%", marginTop: 2, transform: "translateX(-50%)" }}>${r.low}</span>
              <span className="absolute text-[9px] font-mono font-semibold"
                style={{ left: `${mP}%`, bottom: "100%", marginBottom: 2, transform: "translateX(-50%)", color: r.color }}>${r.mid}</span>
              <span className="absolute text-[9px] font-mono text-muted-foreground"
                style={{ left: `${lP + wP}%`, top: "100%", marginTop: 2, transform: "translateX(-50%)" }}>${r.high}</span>
            </div>
          </div>
        );
      })}
      <p className="text-[10px] text-muted-foreground">Dashed line = current market price (${cp.toFixed(2)}). Bar = low–high range. Bold tick = mid estimate.</p>
    </div>
  );
}

// ─── Section Header ────────────────────────────────────────────────────────────
function SectionHeader({ badge, title, subtitle }: { badge: string; title: string; subtitle: string }) {
  return (
    <div>
      <p className="text-xs text-primary font-medium uppercase tracking-widest mb-1">{badge}</p>
      <h2 className="text-2xl font-bold">{title}</h2>
      <p className="text-muted-foreground text-sm mt-1 max-w-2xl">{subtitle}</p>
    </div>
  );
}

// ─── Main App ──────────────────────────────────────────────────────────────────
export default function App() {
  const [inp, setInp] = useState<DCFInputs>(DEFAULT);
  const [cp] = useState(22.5);
  const r = useMemo(() => runDCF(inp), [inp]);
  const set = (k: keyof DCFInputs) => (v: number) => setInp(p => ({ ...p, [k]: v }));
  const fcfData = r.fcfs.map((v, i) => ({ name: `Y${i + 1}`, fcf: +v.toFixed(1) }));
  const go = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <TooltipProvider>
      <Toaster />
      <div className="min-h-screen bg-white text-foreground font-sans">

        {/* ── NAV ─────────────────────────────────────────────────────────────── */}
        <header className="sticky top-0 z-50 bg-white border-b border-border">
          <div className="max-w-5xl mx-auto px-5 h-12 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="font-bold text-sm tracking-tight">
                Valuation<span className="text-primary">Lab</span>
              </span>
              <span className="hidden sm:inline text-muted-foreground text-xs">by Alex Lew, CFA</span>
            </div>
            <nav className="hidden sm:flex items-center gap-4 text-xs text-muted-foreground">
              {[["Curriculum","curriculum"],["DCF","dcf"],["Comps","comps"],["Football Field","ff"],["About","about"]]
                .map(([l, id]) => (
                  <button key={id} onClick={() => go(id)} className="hover:text-primary transition-colors">{l}</button>
                ))}
            </nav>
            <span className="text-[10px] bg-primary/8 text-primary border border-primary/20 px-2 py-0.5 rounded-full font-medium shrink-0">
              Free · Open Access
            </span>
          </div>
        </header>

        <main className="max-w-5xl mx-auto px-5 py-12 space-y-20">

          {/* ── HERO ─────────────────────────────────────────────────────────── */}
          <section className="text-center space-y-5 pt-2">
            <div className="space-y-1">
              <p className="text-xs text-primary font-medium uppercase tracking-widest">Free Valuation Training by Alex Lew, CFA</p>
              <h1 className="text-4xl sm:text-5xl font-bold text-foreground leading-tight">
                Learn to Value<br />Any Company
              </h1>
            </div>
            <p className="text-muted-foreground text-base max-w-xl mx-auto leading-relaxed">
              Full DCF methodology, comparable company analysis, industry deep dives, and
              live Asia-Pacific case studies — all in one page, with working tools.
              No login. No paywall. No videos to produce.
            </p>
            <div className="flex flex-wrap gap-6 justify-center pt-1">
              {[["42","Lessons"],["3","Live Tools"],["6","Case Studies"],["100%","Free"]].map(([v, l]) => (
                <div key={l} className="text-center">
                  <div className="text-2xl font-bold text-foreground font-mono">{v}</div>
                  <div className="text-xs text-muted-foreground">{l}</div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-3 justify-center">
              <button onClick={() => go("curriculum")}
                className="px-5 py-2.5 bg-primary text-primary-foreground text-sm font-semibold rounded-lg hover:opacity-90 transition-opacity">
                Start Learning
              </button>
              <button onClick={() => go("dcf")}
                className="px-5 py-2.5 border border-border text-foreground text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors">
                Try the DCF Tool
              </button>
            </div>
          </section>

          <Separator />

          {/* ── CURRICULUM ───────────────────────────────────────────────────── */}
          <CurriculumSection />

          <Separator />

          {/* ── DCF TOOL ─────────────────────────────────────────────────────── */}
          <section id="dcf" className="space-y-6 scroll-mt-16">
            <SectionHeader badge="Tool 1 — Interactive" title="DCF Valuation Model"
              subtitle="Adjust assumptions with sliders — enterprise value, equity value, price per share, and the full sensitivity table update instantly." />

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              {/* Inputs */}
              <div className="lg:col-span-2 space-y-4">
                <div className="border border-border rounded-lg p-4 space-y-4">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">Revenue & Growth</p>
                  <SRow label="Base Revenue ($M)" value={inp.revenue} unit="M" min={50} max={5000} step={50} onChange={set("revenue")} />
                  <SRow label="Year 1 Growth" value={inp.growth1} unit="%" min={-10} max={60} step={1} onChange={set("growth1")} />
                  <SRow label="Year 2 Growth" value={inp.growth2} unit="%" min={-10} max={50} step={1} onChange={set("growth2")} />
                  <SRow label="Year 3–5 Growth" value={inp.growth3} unit="%" min={-10} max={40} step={1} onChange={set("growth3")} />
                </div>
                <div className="border border-border rounded-lg p-4 space-y-4">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">Margins & FCF</p>
                  <SRow label="EBITDA Margin" value={inp.ebitdaMargin} unit="%" min={-20} max={60} step={1} onChange={set("ebitdaMargin")} />
                  <SRow label="D&A % Revenue"  value={inp.daPercent}    unit="%" min={1}   max={20} step={0.5} onChange={set("daPercent")} />
                  <SRow label="CapEx % Revenue" value={inp.capexPercent} unit="%" min={1}   max={30} step={0.5} onChange={set("capexPercent")} />
                  <SRow label="ΔNWC % Revenue" value={inp.nwcPercent}   unit="%" min={-5}  max={15} step={0.5} onChange={set("nwcPercent")} />
                  <SRow label="Tax Rate"        value={inp.taxRate}      unit="%" min={5}   max={40} step={1}   onChange={set("taxRate")} />
                </div>
                <div className="border border-border rounded-lg p-4 space-y-4">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">Discount Rate</p>
                  <SRow label="WACC"             value={inp.wacc}    unit="%" min={5}    max={20}   step={0.5} onChange={set("wacc")} />
                  <SRow label="Terminal Growth"  value={inp.tgr}     unit="%" min={0}    max={5}    step={0.25} onChange={set("tgr")} />
                  <SRow label="Net Debt ($M)"    value={inp.netDebt} unit="M" min={-500} max={3000} step={25}  onChange={set("netDebt")} />
                  <SRow label="Shares Out. (M)"  value={inp.shares}  unit="M" min={10}   max={1000} step={10}  onChange={set("shares")} />
                </div>
              </div>

              {/* Outputs */}
              <div className="lg:col-span-3 space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { l: "Enterprise Value", v: fmt(r.ev), primary: false },
                    { l: "Equity Value",     v: fmt(r.eq), primary: false },
                    { l: "Price / Share",    v: `$${r.pps.toFixed(2)}`, primary: true },
                    { l: "TV % of EV",       v: `${r.tvPct.toFixed(0)}%`, primary: false },
                  ].map(m => (
                    <div key={m.l} className={`border rounded-lg p-3 ${m.primary ? "border-primary/40 bg-primary/5" : "border-border bg-gray-50/60"}`}>
                      <p className="text-[10px] text-muted-foreground mb-0.5">{m.l}</p>
                      <p className={`text-lg font-bold font-mono tabular-nums ${m.primary ? "text-primary" : "text-foreground"}`}>{m.v}</p>
                    </div>
                  ))}
                </div>

                <div className="border border-border rounded-lg p-4">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest mb-3">EV Bridge</p>
                  <div className="space-y-1.5 text-xs font-mono">
                    {[
                      { l: "PV of FCFs (Y1–5)",    v: r.pvFCF,      bold: false, div: false },
                      { l: "PV of Terminal Value", v: r.pvTV,       bold: false, div: true  },
                      { l: "Enterprise Value",     v: r.ev,         bold: true,  div: false },
                      { l: "(−) Net Debt",         v: -inp.netDebt, bold: false, div: true  },
                      { l: "Equity Value",         v: r.eq,         bold: true,  div: false, primary: true },
                    ].map((row, i) => (
                      <div key={i}>
                        <div className={`flex justify-between py-0.5 ${row.bold ? "font-bold" : "text-muted-foreground"}`}>
                          <span className="font-sans">{row.l}</span>
                          <span className={`tabular-nums ${row.primary ? "text-primary" : row.v < 0 ? "text-rose-600" : ""}`}>
                            {row.v >= 0 ? "+" : ""}{fmt(row.v)}
                          </span>
                        </div>
                        {row.div && <Separator className="my-1.5" />}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="border border-border rounded-lg p-4">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest mb-3">Projected FCFF ($M)</p>
                  <ResponsiveContainer width="100%" height={130}>
                    <BarChart data={fcfData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.89 0.008 220)" />
                      <XAxis dataKey="name" tick={{ fontSize: 10, fill: "oklch(0.50 0.012 220)" }} />
                      <YAxis tick={{ fontSize: 10, fill: "oklch(0.50 0.012 220)" }} />
                      <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 6, fontSize: 11 }}
                        formatter={(v: number) => [`$${v.toFixed(1)}M`, "FCF"]} />
                      <Bar dataKey="fcf" radius={[3, 3, 0, 0]}>
                        {fcfData.map((d, i) => <Cell key={i} fill={d.fcf >= 0 ? "oklch(0.38 0.14 230)" : "oklch(0.55 0.22 10)"} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="border border-border rounded-lg p-4">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest mb-3">Sensitivity: WACC vs. Terminal Growth</p>
                  <SensTable inp={inp} />
                </div>
              </div>
            </div>
          </section>

          <Separator />

          {/* ── COMPS ────────────────────────────────────────────────────────── */}
          <section id="comps" className="space-y-6 scroll-mt-16">
            <SectionHeader badge="Tool 2 — Interactive" title="Comparable Companies Table"
              subtitle="Edit any cell — EV/Revenue, EV/EBITDA, and P/E multiples recalculate automatically. Pre-loaded with Southeast Asia tech comps." />
            <div className="border border-border rounded-xl p-5 bg-white">
              <CompsTable />
            </div>
            <div className="rounded-lg bg-gray-50 border border-border p-4 text-xs text-muted-foreground leading-relaxed">
              <strong className="text-foreground">How to use: </strong>
              Select the multiple that best fits your sector (EV/EBITDA for profitable companies; EV/Revenue for pre-profit or high-growth).
              Apply the median to your target company's metrics. Then layer in the football field below to visualise the full range.
            </div>
          </section>

          <Separator />

          {/* ── FOOTBALL FIELD ───────────────────────────────────────────────── */}
          <section id="ff" className="space-y-6 scroll-mt-16">
            <SectionHeader badge="Tool 3 — Visual" title="Valuation Football Field"
              subtitle="The football field chart visualises how different valuation methodologies produce different implied share prices — and where the market price sits within that range." />
            <div className="border border-border rounded-xl p-6 bg-white">
              <FootballBar ranges={FF_DEFAULT} cp={cp} />
            </div>
            <div className="border border-border rounded-xl p-5 bg-white">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest mb-3">Mid-Point Comparison</p>
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={FF_DEFAULT.map(r => ({ name: r.label.replace(" Comps",""), mid: r.mid, color: r.color }))}
                  margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.89 0.008 220)" />
                  <XAxis dataKey="name" tick={{ fontSize: 9, fill: "oklch(0.50 0.012 220)" }} />
                  <YAxis tick={{ fontSize: 9, fill: "oklch(0.50 0.012 220)" }} />
                  <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 6, fontSize: 11 }}
                    formatter={(v: number) => [`$${v.toFixed(2)}`, "Mid estimate"]} />
                  <ReferenceLine y={cp} stroke="#9ca3af" strokeDasharray="4 2"
                    label={{ value: "Mkt", fill: "#9ca3af", fontSize: 9 }} />
                  <Bar dataKey="mid" radius={[3, 3, 0, 0]}>
                    {FF_DEFAULT.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>

          <Separator />

          {/* ── ABOUT ────────────────────────────────────────────────────────── */}
          <section id="about" className="space-y-6 scroll-mt-16">
            <SectionHeader badge="About" title="Alex Lew, CFA" subtitle="" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { title: "Background", body: "Oxford alumnus with senior management experience across healthtech and fintech in Asia-Pacific. This platform is a voluntary contribution to financial education — tools and written content made freely available to anyone learning investment analysis." },
                { title: "Why This Platform", body: "Professional-grade valuation tools and written curriculum should not sit behind a $1,000 paywall. This site offers a working DCF calculator, a comparable companies builder, a football field chart, and 40+ written lessons — all free, no registration, no email required." },
                { title: "Investment Analysis Focus", body: "Every lesson, formula, and case study on this site is grounded in real investment analysis practice — the same work that goes into equity research, M&A due diligence, and portfolio construction. The goal is to close the gap between textbook theory and what analysts actually do at their desks." },
                { title: "Asia-Pacific Focus", body: "Most free valuation resources are US-centric. Case studies here use real SEA and HK companies — Sea Limited, Grab, AIA — using actual public filings from SGX, HKEX, and SEC EDGAR. Multiples and risk premiums reflect APAC market conditions, not US defaults." },
              ].map(c => (
                <div key={c.title} className="border border-border rounded-xl p-5">
                  <h3 className="font-semibold text-sm mb-2">{c.title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{c.body}</p>
                </div>
              ))}
            </div>
          </section>

        </main>

        {/* ── FOOTER ───────────────────────────────────────────────────────────── */}
        <footer className="border-t border-border bg-gray-50/60 py-6 mt-16">
          <div className="max-w-5xl mx-auto px-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-muted-foreground">
            <span>ValuationLab by <strong className="text-foreground">Alex Lew, CFA</strong></span>
            <span>Free · No login required · No paywall</span>
          </div>
        </footer>

      </div>
    </TooltipProvider>
  );
}
